import PageSEO from '../../components/feature/PageSEO';
import Footer from '../home/components/Footer';

export default function ServicesPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://arvakon.com';

  const servicesJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Technical Verification Services – Arvakon',
    url: `${siteUrl}/services`,
    provider: {
      '@type': 'Organization',
      name: 'Arvakon',
      url: siteUrl,
    },
    description: 'Independent verification and surveillance services for industrial manufacturing: manufacturing surveillance, welding oversight, NDT witness, material traceability, protective coating inspection, and supplier surveillance.',
    serviceType: 'Third-party technical verification',
    areaServed: 'International',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Arvakon Technical Verification Services',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Independent Manufacturing Surveillance' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Welding Oversight and Procedure Review' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Non-Destructive Testing Witness & Review' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Material Traceability Verification' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Protective Coating Inspection' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Supplier Surveillance & Sub-tier Oversight' } },
      ],
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <PageSEO
        title="Technical Verification Services – Arvakon | Manufacturing Surveillance & NDT"
        description="Independent manufacturing surveillance, welding oversight, NDT witness, material traceability, protective coating inspection, and supplier surveillance services. ASME, ISO, EN, NORSOK aligned."
        keywords="manufacturing surveillance, welding oversight, NDT witness, material traceability, coating inspection, supplier surveillance, ASME, ISO 3834"
        canonical={`${siteUrl}/services`}
        jsonLd={servicesJsonLd}
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <a href="/" className="text-dark-primary text-xl font-semibold tracking-tight">
            ARVAKON
          </a>
          <nav className="flex items-center gap-8">
            <a href="/" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">
              Home
            </a>
            <a href="/services" className="text-sm text-dark-primary font-medium">
              Services
            </a>
            <a href="/standards" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">
              Standards
            </a>
            <a href="/projects" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">
              Industry Applications
            </a>
            <a href="/about" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">
              About
            </a>
            <a href="/contact" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">
              Contact
            </a>
          </nav>
        </div>
      </header>

      {/* Page Header */}
      <section className="pt-40 pb-20 px-8 bg-dark-primary">
        <div className="max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-industrial-lighter/20 rounded-full mb-8">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-file-list-3-line text-industrial-lighter text-sm"></i>
            </div>
            <span className="text-industrial-lighter text-xs font-medium tracking-[0.12em] uppercase">
              Service Scope Documentation
            </span>
          </div>
          <h1 className="font-serif text-white text-5xl leading-tight mb-8">
            Technical Verification Services
          </h1>
          <p className="text-industrial-light text-base leading-[1.8] max-w-3xl">
            Independent verification and surveillance services for industrial manufacturing operations.
            Services are delivered through structured inspection and test plans developed in coordination
            with client quality requirements. Activities reference internationally recognized standards
            and client-approved specifications. Services do not constitute certification or approval authority.
          </p>
          <p className="text-industrial-light/50 text-xs leading-[1.8] max-w-3xl mt-5">
            A structured service scope summary may be provided in printable format upon request for procurement and vendor qualification documentation purposes.
          </p>
        </div>
      </section>

      {/* Service 1 */}
      <section className="py-28 px-8 bg-white relative">
        {/* Subtle background pattern - welding seam */}
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(90deg, #64748b 0px, transparent 2px, transparent 40px)' }}></div>
        
        <div className="relative max-w-6xl mx-auto">
          <h2 className="font-serif text-dark-primary text-4xl mb-12 leading-tight">
            Independent Manufacturing Surveillance
          </h2>

          <div className="mb-10">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Technical Scope
            </h3>
            <p className="text-industrial-steel text-sm leading-[1.8]">
              On-site presence during fabrication operations to verify compliance with approved manufacturing procedures,
              dimensional requirements, and process specifications. Surveillance activities occur at defined hold points
              and witness points per client-approved inspection and test plans. Services include real-time observation
              of fabrication processes, verification of dimensional control, and documentation of manufacturing activities
              against specified requirements.
            </p>
          </div>

          <div className="mb-10">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Typical Inspection Activities
            </h3>
            <ul className="space-y-3.5">
              {[
                'Verification of manufacturing drawings and procedure compliance',
                'Dimensional inspection and fit-up verification prior to welding',
                'Witness of critical fabrication operations and assembly sequences',
                'Review of manufacturing non-conformance reports and corrective actions',
                'Documentation of hold point releases and witness point observations',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-industrial-steel text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-industrial-steel mt-2 flex-shrink-0"></div>
                  <span className="leading-[1.75]">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-10">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Applicable Standards (Reference Framework)
            </h3>
            <div className="flex flex-wrap gap-3">
              {['ASME Section VIII', 'EN 13445', 'ISO 3834 (principles)', 'PED 2014/68/EU'].map((s) => (
                <span key={s} className="px-5 py-2.5 bg-light-section border border-light-border rounded-md text-dark-primary text-xs font-medium">
                  {s}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-light-section border border-light-border border-l-2 border-l-industrial-steel/40 rounded-r-lg p-8">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Reporting Structure &amp; Authority Limitations
            </h3>
            <p className="text-industrial-steel text-sm leading-[1.8] mb-5">
              Inspection reports document observed manufacturing conditions and dimensional verification results.
              Reports are formatted per client requirements and provided for use in quality assurance documentation packages.
            </p>
            <p className="text-industrial-muted text-sm leading-[1.8]">
              Final acceptance authority remains with equipment purchaser or designated certification body.
              Services consist of independent observation and reporting; they do not constitute design approval
              or regulatory authorization.
            </p>
          </div>
        </div>
      </section>

      {/* Service 2 */}
      <section className="py-28 px-8 bg-light-section relative">
        {/* Subtle background pattern - ultrasonic waves */}
        <div className="absolute inset-0 opacity-[0.015] pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 30px, #64748b 30px, transparent 32px)' }}></div>
        
        <div className="relative max-w-6xl mx-auto">
          <h2 className="font-serif text-dark-primary text-4xl mb-12 leading-tight">
            Welding Oversight and Procedure Review
          </h2>

          <div className="mb-10">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Technical Scope
            </h3>
            <p className="text-industrial-steel text-sm leading-[1.8]">
              Review of welding procedure specifications, welder qualification records, and welding consumable
              traceability documentation. Witness of procedure qualification testing and production welding activities.
              Verification of welding process parameters, preheat and interpass temperature control, and post-weld
              heat treatment operations. Services assess welding quality requirements against ISO 3834 principles
              and applicable code requirements.
            </p>
          </div>

          <div className="mb-10">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Typical Inspection Activities
            </h3>
            <ul className="space-y-3.5">
              {[
                'Review of welding procedure specifications and procedure qualification records',
                'Verification of welder qualification records and continuity documentation',
                'Witness of procedure qualification testing and destructive examination',
                'Verification of welding consumable traceability and storage conditions',
                'Monitoring of preheat, interpass temperature, and post-weld heat treatment operations',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-industrial-steel text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-industrial-steel mt-2 flex-shrink-0"></div>
                  <span className="leading-[1.75]">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-10">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Applicable Standards (Reference Framework)
            </h3>
            <div className="flex flex-wrap gap-3">
              {['ASME Section IX', 'ISO 3834 (principles)', 'AWS D1.1', 'EN ISO 15614'].map((s) => (
                <span key={s} className="px-5 py-2.5 bg-white border border-light-border rounded-md text-dark-primary text-xs font-medium">
                  {s}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-white border border-light-border border-l-2 border-l-industrial-steel/40 rounded-r-lg p-8">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Reporting Structure &amp; Authority Limitations
            </h3>
            <p className="text-industrial-steel text-sm leading-[1.8] mb-5">
              Welding oversight reports document procedure review findings, qualification record verification,
              and witnessed welding activities. Reports include consumable traceability verification and
              process parameter observations.
            </p>
            <p className="text-industrial-muted text-sm leading-[1.8]">
              Welding procedure approval authority remains with client or designated welding engineer.
              Services provide independent verification of procedure compliance and qualification documentation;
              they do not constitute procedure approval or welder certification.
            </p>
          </div>
        </div>
      </section>

      {/* Service 3 */}
      <section className="py-28 px-8 bg-white relative">
        {/* Subtle background pattern - coating layers */}
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(0deg, #64748b 0px, transparent 1px, transparent 20px, #64748b 20px, transparent 21px, transparent 40px)' }}></div>
        
        <div className="relative max-w-6xl mx-auto">
          <h2 className="font-serif text-dark-primary text-4xl mb-12 leading-tight">
            Non-Destructive Testing Witness &amp; Review
          </h2>

          <div className="mb-10">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Technical Scope
            </h3>
            <p className="text-industrial-steel text-sm leading-[1.8]">
              Witness of radiographic, ultrasonic, magnetic particle, and liquid penetrant testing activities.
              Review of NDT procedures, operator qualification records, and equipment calibration documentation.
              Verification of acceptance criteria application per applicable codes and project specifications.
              Observation of testing technique, coverage verification, and documentation of test results against
              specified acceptance standards.
            </p>
          </div>

          <div className="mb-10">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Typical Inspection Activities
            </h3>
            <ul className="space-y-3.5">
              {[
                'Review of NDT procedures and operator qualification documentation',
                'Verification of equipment calibration records and reference standard traceability',
                'Witness of radiographic exposure setup and film processing operations',
                'Observation of ultrasonic testing technique and coverage verification',
                'Review of test reports and acceptance criteria application',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-industrial-steel text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-industrial-steel mt-2 flex-shrink-0"></div>
                  <span className="leading-[1.75]">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-10">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Applicable Standards (Reference Framework)
            </h3>
            <div className="flex flex-wrap gap-3">
              {['ASME Section V', 'ISO 9712', 'EN ISO 17640', 'ASTM E1417'].map((s) => (
                <span key={s} className="px-5 py-2.5 bg-light-section border border-light-border rounded-md text-dark-primary text-xs font-medium">
                  {s}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-light-section border border-light-border border-l-2 border-l-industrial-steel/40 rounded-r-lg p-8">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Reporting Structure &amp; Authority Limitations
            </h3>
            <p className="text-industrial-steel text-sm leading-[1.8] mb-5">
              NDT witness reports document observed testing activities, procedure compliance verification,
              and test result review findings. Reports reference applicable acceptance criteria and document
              any observed deviations from specified procedures.
            </p>
            <p className="text-industrial-muted text-sm leading-[1.8]">
              Final interpretation of test results and acceptance decisions remain with client or designated
              NDT Level III authority. Services consist of independent observation and documentation; they do
              not constitute test result interpretation or acceptance authority.
            </p>
          </div>
        </div>
      </section>

      {/* Service 4 */}
      <section className="py-28 px-8 bg-light-section relative">
        {/* Subtle background pattern - grid structure */}
        <div className="absolute inset-0 opacity-[0.015] pointer-events-none" style={{ backgroundImage: 'linear-gradient(#64748b 1px, transparent 1px), linear-gradient(90deg, #64748b 1px, transparent 1px)', backgroundSize: '80px 80px' }}></div>
        
        <div className="relative max-w-6xl mx-auto">
          <h2 className="font-serif text-dark-primary text-4xl mb-12 leading-tight">
            Material Traceability Verification
          </h2>

          <div className="mb-10">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Technical Scope
            </h3>
            <p className="text-industrial-steel text-sm leading-[1.8]">
              Review of material certifications, mill test reports, and heat traceability documentation.
              Verification of material specifications per ASME Section II, EN 10204, or project-specific
              requirements. Witness of positive material identification testing and verification of material
              marking and identification systems. Documentation of material traceability from mill certification
              through fabrication and final assembly.
            </p>
          </div>

          <div className="mb-10">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Typical Inspection Activities
            </h3>
            <ul className="space-y-3.5">
              {[
                'Review of mill test reports and material certification documentation',
                'Verification of material specifications against project requirements',
                'Witness of positive material identification testing using PMI equipment',
                'Verification of material marking systems and heat number traceability',
                'Documentation of material traceability through fabrication processes',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-industrial-steel text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-industrial-steel mt-2 flex-shrink-0"></div>
                  <span className="leading-[1.75]">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-10">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Applicable Standards (Reference Framework)
            </h3>
            <div className="flex flex-wrap gap-3">
              {['ASME Section II', 'EN 10204', 'ASTM A20', 'ISO 10474'].map((s) => (
                <span key={s} className="px-5 py-2.5 bg-white border border-light-border rounded-md text-dark-primary text-xs font-medium">
                  {s}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-white border border-light-border border-l-2 border-l-industrial-steel/40 rounded-r-lg p-8">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Reporting Structure &amp; Authority Limitations
            </h3>
            <p className="text-industrial-steel text-sm leading-[1.8] mb-5">
              Material verification reports document certification review findings, PMI test results, and
              traceability verification activities. Reports include material specification compliance verification
              and heat number traceability documentation.
            </p>
            <p className="text-industrial-muted text-sm leading-[1.8]">
              Material acceptance authority remains with equipment purchaser or designated materials engineer.
              Services provide independent verification of material documentation and traceability; they do not
              constitute material approval or specification authority.
            </p>
          </div>
        </div>
      </section>

      {/* Service 5 */}
      <section className="py-28 px-8 bg-white relative">
        {/* Subtle background pattern - coating layers */}
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(0deg, #64748b 0px, transparent 1px, transparent 15px, #64748b 15px, transparent 16px, transparent 30px, #64748b 30px, transparent 31px, transparent 45px)' }}></div>
        
        <div className="relative max-w-6xl mx-auto">
          <h2 className="font-serif text-dark-primary text-4xl mb-12 leading-tight">
            Protective Coating Inspection (ISO 12944 / NORSOK M-501 aligned)
          </h2>

          <div className="mb-10">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Technical Scope
            </h3>
            <p className="text-industrial-steel text-sm leading-[1.8]">
              Coating inspection activities are performed in accordance with ISO 12944 and NORSOK M-501 project
              specifications. Inspection scope includes surface preparation verification, coating application
              monitoring, dry film thickness measurement, and holiday detection testing. Activities verify
              compliance with project coating specifications, application procedures, and environmental control
              requirements during surface preparation and coating application operations.
            </p>
          </div>

          <div className="mb-10">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Typical Inspection Activities
            </h3>
            <ul className="space-y-3.5">
              {[
                'Verification of surface preparation standards using visual and comparator methods',
                'Measurement of surface cleanliness, dust levels, and environmental conditions',
                'Monitoring of coating application procedures and mixing ratio verification',
                'Dry film thickness measurement using magnetic and electronic gauges',
                'Holiday detection testing and documentation of coating defects',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-industrial-steel text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-industrial-steel mt-2 flex-shrink-0"></div>
                  <span className="leading-[1.75]">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-10">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Applicable Standards (Reference Framework)
            </h3>
            <div className="flex flex-wrap gap-3">
              {['ISO 12944', 'NORSOK M-501', 'ISO 8501-1', 'SSPC-PA 2'].map((s) => (
                <span key={s} className="px-5 py-2.5 bg-light-section border border-light-border rounded-md text-dark-primary text-xs font-medium">
                  {s}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-light-section border border-light-border border-l-2 border-l-industrial-steel/40 rounded-r-lg p-8">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Reporting Structure &amp; Authority Limitations
            </h3>
            <p className="text-industrial-steel text-sm leading-[1.8] mb-5">
              Coating inspection reports document surface preparation verification, coating application observations,
              dry film thickness measurements, and holiday detection test results. Reports are formatted per project
              coating specification requirements.
            </p>
            <p className="text-industrial-muted text-sm leading-[1.8]">
              Final coating acceptance authority remains with equipment purchaser or designated coating engineer.
              Services provide independent observation and measurement documentation; they do not constitute coating
              system approval or specification authority.
            </p>
          </div>
        </div>
      </section>

      {/* Service 6 */}
      <section className="py-28 px-8 bg-light-section relative pb-24">
        {/* Subtle background pattern - supply chain grid */}
        <div className="absolute inset-0 opacity-[0.015] pointer-events-none" style={{ backgroundImage: 'linear-gradient(#64748b 1px, transparent 1px), linear-gradient(90deg, #64748b 1px, transparent 1px)', backgroundSize: '100px 100px' }}></div>
        
        <div className="relative max-w-6xl mx-auto">
          <h2 className="font-serif text-dark-primary text-4xl mb-12 leading-tight">
            Supplier Surveillance &amp; Sub-tier Oversight
          </h2>

          <div className="mb-10">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Technical Scope
            </h3>
            <p className="text-industrial-steel text-sm leading-[1.8]">
              Independent verification services at sub-tier supplier facilities and component manufacturers.
              Surveillance of manufacturing operations, material procurement, and quality control activities at
              supply chain facilities. Verification of supplier qualification documentation, manufacturing procedures,
              and sub-tier material certifications. Services provide supply chain visibility and documentation of
              manufacturing activities at facilities beyond primary fabrication locations.
            </p>
          </div>

          <div className="mb-10">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Typical Inspection Activities
            </h3>
            <ul className="space-y-3.5">
              {[
                'Review of supplier qualification documentation and quality system records',
                'Verification of sub-tier material certifications and traceability documentation',
                'Witness of component manufacturing and testing at sub-tier facilities',
                'Documentation of supplier manufacturing procedures and process controls',
                'Compilation of sub-tier documentation packages for client quality records',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-industrial-steel text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-industrial-steel mt-2 flex-shrink-0"></div>
                  <span className="leading-[1.75]">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-10">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Applicable Standards (Reference Framework)
            </h3>
            <div className="flex flex-wrap gap-3">
              {['ASME NQA-1', 'ISO 3834 (principles)', 'EN 10204', 'Client QA Requirements'].map((s) => (
                <span key={s} className="px-5 py-2.5 bg-white border border-light-border rounded-md text-dark-primary text-xs font-medium">
                  {s}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-white border border-light-border border-l-2 border-l-industrial-steel/40 rounded-r-lg p-8">
            <h3 className="text-dark-primary text-xs font-semibold uppercase tracking-[0.12em] mb-5">
              Reporting Structure &amp; Authority Limitations
            </h3>
            <p className="text-industrial-steel text-sm leading-[1.8] mb-5">
              Supplier surveillance reports document sub-tier manufacturing activities, qualification verification
              findings, and material traceability documentation. Reports provide supply chain visibility and
              documentation for client quality assurance systems.
            </p>
            <p className="text-industrial-muted text-sm leading-[1.8]">
              Supplier approval authority remains with equipment purchaser or designated procurement authority.
              Services provide independent verification of supplier activities and documentation; they do not
              constitute supplier qualification or approval authority.
            </p>
          </div>
        </div>
      </section>

      {/* Service Scope Clarifications */}
      <section className="py-28 px-8 bg-white pb-24">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 pb-3 border-b border-slate-200 mb-0">
            Service Scope Clarifications
          </h2>
          <div className="space-y-0">
            {[
              {
                q: 'Does Arvakon replace the manufacturer\'s quality control system?',
                a: 'No. Arvakon performs independent verification activities in parallel to manufacturer quality systems. Responsibility for manufacturing execution and internal quality control remains with the manufacturer.',
              },
              {
                q: 'Does Arvakon provide engineering design approval?',
                a: 'No. Arvakon does not perform engineering design approval or technical certification. Verification activities are limited to observation, documentation review, and reporting within the agreed scope.',
              },
              {
                q: 'Can Arvakon define inspection criteria independently?',
                a: 'Inspection criteria are defined by applicable standards, contractual specifications, and client-approved inspection and test plans. Arvakon operates within these predefined frameworks.',
              },
              {
                q: 'Is Arvakon responsible for final equipment acceptance?',
                a: 'Final acceptance authority remains with the equipment purchaser, project owner, or designated certification body.',
              },
            ].map((item, index, arr) => (
              <div
                key={index}
                className={`grid grid-cols-2 gap-12 py-8 ${index < arr.length - 1 ? 'border-b border-slate-200' : ''}`}
              >
                <p className="text-sm font-medium text-dark-primary leading-7">
                  {item.q}
                </p>
                <p className="text-sm text-slate-600 leading-7">
                  {item.a}
                </p>
              </div>
            ))}
          </div>

          {/* Governance & Independence */}
          <div className="mt-16 pt-8 border-t border-slate-200">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-5">
              Governance &amp; Independence
            </h2>
            <p className="text-sm text-slate-500 leading-[1.8] mb-3">
              All service activities are performed under Arvakon's structured governance framework ensuring technical impartiality and defined authority boundaries.
            </p>
            <p className="text-sm text-slate-500 leading-[1.8]">
              For detailed governance principles, please refer to the{' '}
              <a href="/about" className="text-dark-primary underline underline-offset-2 hover:opacity-70 transition-opacity">
                About Arvakon
              </a>{' '}
              page.
            </p>
          </div>

          {/* Reporting & Deliverables */}
          <div className="mt-16 pt-8 border-t border-slate-200">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-5">
              Reporting &amp; Deliverables
            </h2>
            <p className="text-sm text-slate-500 leading-[1.8] mb-5">
              Arvakon provides structured technical reporting aligned with project-specific inspection and test plans.
            </p>
            <p className="text-sm text-slate-500 leading-[1.8] mb-3">
              Typical deliverables may include:
            </p>
            <ul className="space-y-2 mb-5">
              {[
                'Inspection visit reports documenting observed activities',
                'Non-conformance observation summaries (where applicable)',
                'Material traceability verification records',
                'Welding and NDT documentation review confirmation',
                'Coating inspection measurement records',
                'Final structured verification summary report',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-slate-500 text-sm">
                  <div className="w-1 h-1 rounded-full bg-slate-400 mt-2.5 flex-shrink-0"></div>
                  <span className="leading-[1.8]">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-slate-500 leading-[1.8] mb-2">
              All reporting is issued for client review and contractual documentation purposes.
            </p>
            <p className="text-sm text-slate-500 leading-[1.8] mb-2">
              Final acceptance authority remains with the equipment purchaser or designated authority.
            </p>
            <p className="text-sm text-slate-500 leading-[1.8]">
              For structured documentation requests, please submit a technical inquiry through the{' '}
              <a href="/contact" className="text-dark-primary underline underline-offset-2 hover:opacity-70 transition-opacity">
                Contact page
              </a>.
            </p>
            <p className="text-xs text-slate-400 leading-[1.8] mt-3">
              Reporting structure version: RDS-01 | Aligned with ARV-VQD-001 Rev.1
            </p>
          </div>

          {/* Procurement Documentation */}
          <div className="mt-16 pt-8 border-t border-slate-200">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-5">
              Procurement Documentation
            </h2>
            <p className="text-sm text-slate-500 leading-[1.8] mb-3">
              Structured documentation summaries related to service scope and verification activities may be provided upon formal request for procurement and vendor qualification processes.
            </p>
            <p className="text-sm text-slate-500 leading-[1.8] mb-3">
              For documentation requests, please use the{' '}
              <a href="/contact" className="text-dark-primary underline underline-offset-2 hover:opacity-70 transition-opacity">
                Technical Inquiry
              </a>{' '}
              form.
            </p>
            <p className="text-xs text-slate-400 leading-[1.8]">
              Documentation reference: ARV-VQD-001 Rev.1 | Issue Date: 2025-03-02
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}